import mergeRefs from 'merge-refs';
import { observer } from 'mobx-react-lite';
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { askUserStore } from './ask-user.store';
import { FreeTextRow, OptionRow, RowDivider } from './AskUserRows';

import type { AskUserRequest } from '@littlebird/sdk';

const ignorePickerNavigationHotkey = (event: KeyboardEvent) =>
  event.isComposing ||
  (event.target instanceof HTMLInputElement &&
    (event.key === 'ArrowLeft' || event.key === 'ArrowRight'));

const ignoreComposing = (event: KeyboardEvent) => event.isComposing;

const useAskUserPickerHotkeys = ({
  pickerRef,
  requestId,
  currentIndex,
  options,
  onNavigate,
  onSelectOption,
  onAdvance,
  disabled,
  autoFocus,
}: {
  pickerRef: React.RefObject<HTMLDivElement | null>;
  requestId: string;
  currentIndex: number;
  options: string[];
  onNavigate: (index: number) => void;
  onSelectOption: (option: string) => void;
  onAdvance: () => void;
  disabled: boolean;
  autoFocus: boolean;
}) => {
  const navigationHotkeysRef = useHotkeys<HTMLDivElement>(
    'left,right,up,down,enter',
    (event) => {
      event.stopPropagation();

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        onNavigate(currentIndex + (event.key === 'ArrowLeft' ? -1 : 1));
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const optionElements = [
          ...(pickerRef.current?.querySelectorAll<HTMLButtonElement>(
            '.ask-user-option'
          ) ?? []),
        ];
        if (!optionElements.length) return;

        const focusedIndex = optionElements.indexOf(
          document.activeElement as HTMLButtonElement
        );
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex =
          focusedIndex === -1
            ? step === 1
              ? 0
              : optionElements.length - 1
            : Math.max(
                0,
                Math.min(optionElements.length - 1, focusedIndex + step)
              );
        optionElements[nextIndex].focus();
        return;
      }

      if (event.target instanceof HTMLInputElement) {
        onAdvance();
      } else if (event.target instanceof HTMLButtonElement) {
        event.target.click();
      }
    },
    {
      enabled: !disabled,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      ignoreEventWhen: ignorePickerNavigationHotkey,
    }
  );

  const numberHotkeysRef = useHotkeys<HTMLDivElement>(
    '1,2,3,4,5,6,7,8,9',
    (event) => {
      const option = options[Number.parseInt(event.key, 10) - 1];
      if (!option) return;

      event.stopPropagation();
      onSelectOption(option);
    },
    {
      enabled: !disabled,
      preventDefault: true,
      ignoreEventWhen: ignoreComposing,
    }
  );

  const hotkeysRef = useMemo(
    () => mergeRefs(pickerRef, navigationHotkeysRef, numberHotkeysRef),
    [pickerRef, navigationHotkeysRef, numberHotkeysRef]
  );

  useEffect(() => {
    // Focus the user moved into the picker, as by tabbing to an option, stays where they put it
    if (autoFocus && !pickerRef.current?.contains(document.activeElement))
      pickerRef.current?.focus({ preventScroll: true });
  }, [pickerRef, requestId, autoFocus]);

  return hotkeysRef;
};

export const AskUserPicker = observer(
  ({
    request,
    onComplete,
    renderHeader,
    context,
    disabled = false,
    autoFocus = true,
  }: {
    request: AskUserRequest;
    onComplete?: () => void;
    renderHeader?: (
      title?: string,
      actions?: ReactNode,
      bodyShowsReason?: boolean
    ) => ReactNode;
    // What the user needs to know to answer, drawn in full under the header
    context?: ReactNode;
    disabled?: boolean;
    // False where the picker is drawn without being selected, so it leaves focus where it is
    autoFocus?: boolean;
  }) => {
    const requestId = request.request_id;
    const total = request.questions.length;

    const [index, setIndex] = useState(0);
    const pickerRef = useRef<HTMLDivElement>(null);

    const clampedIndex = Math.min(index, total - 1);
    const question = request.questions[clampedIndex];

    const goTo = (next: number) => {
      const clamped = Math.max(0, Math.min(total - 1, next));
      pickerRef.current?.focus({ preventScroll: true });
      setIndex(clamped);
    };

    const isLastQuestion = clampedIndex >= total - 1;
    // Advancing past the last question finishes the flow, which sends.
    const advanceOrComplete = () => {
      if (isLastQuestion) onComplete?.();
      else goTo(clampedIndex + 1);
    };

    const answer = question
      ? askUserStore.getAnswer(requestId, question.id)
      : undefined;
    const isSingleSelect = !question?.allow_multiple;
    const pickedCount =
      (answer?.selected.length ?? 0) + (answer?.freeText ? 1 : 0);

    const pickOption = (option: string) => {
      if (!question) return;

      const wasSelected = answer?.selected.includes(option) ?? false;
      askUserStore.toggleOption(
        requestId,
        question.id,
        option,
        question.allow_multiple
      );
      // Single-select is a pick-and-move-on flow; advance only on a new selection
      if (isSingleSelect && !wasSelected) advanceOrComplete();
    };

    const skipQuestion = () => {
      if (!question) return;

      askUserStore.skipQuestion(requestId, question.id);
      advanceOrComplete();
    };

    const pickerHotkeysRef = useAskUserPickerHotkeys({
      pickerRef,
      requestId,
      currentIndex: clampedIndex,
      options: question?.options ?? [],
      onNavigate: goTo,
      onSelectOption: pickOption,
      onAdvance: advanceOrComplete,
      disabled,
      autoFocus,
    });

    if (!question) return null;

    const pager = (
      <AskUserQuestionPager
        currentIndex={clampedIndex}
        total={total}
        showComplete={isSingleSelect && isLastQuestion}
        canComplete={askUserStore.hasAnyAnswer(requestId)}
        onPrevious={() => goTo(clampedIndex - 1)}
        onNext={() => goTo(clampedIndex + 1)}
        onComplete={onComplete}
      />
    );

    return (
      <YStack
        ref={pickerHotkeysRef}
        role="group"
        tabIndex={0}
        className={cn(
          'focus:outline-none',
          renderHeader
            ? '-mb-[var(--ac-frame-pb)] gap-[var(--ac-frame-gap)]'
            : 'border-border-strong bg-surface mb-2 max-h-96 overflow-x-clip overflow-y-auto rounded-xl border'
        )}
      >
        {renderHeader?.(
          question.prompt,
          <fieldset
            role="presentation"
            className="flex items-center gap-1"
            disabled={disabled}
            inert={disabled}
          >
            {pager}
          </fieldset>,
          Boolean(context)
        )}
        {renderHeader ? context : null}
        <fieldset
          key={question.id}
          role="presentation"
          className="min-w-0"
          disabled={disabled}
          inert={disabled}
          aria-busy={disabled}
        >
          {!renderHeader && (
            <XStack className="items-start justify-between gap-4 px-4 pt-3 pb-2">
              <span className="text-foreground min-w-0 text-base font-medium">
                {question.prompt}
              </span>
              <XStack className="text-foreground-muted shrink-0 items-center gap-1">
                {pager}
                <IconButton
                  icon={Icons.cancel}
                  size="xs"
                  variant="ghost"
                  data-testid="ask-user-skip-all"
                  className="ml-1"
                  onClick={() => {
                    askUserStore.skipAll(request);
                    onComplete?.();
                  }}
                />
              </XStack>
            </XStack>
          )}

          <YStack className="px-2 pb-2">
            {question.options.map((option, optionIndex) => {
              const isSelected = answer?.selected.includes(option) ?? false;
              return (
                <Fragment key={option}>
                  {optionIndex > 0 && <RowDivider />}
                  <OptionRow
                    number={optionIndex + 1}
                    label={option}
                    isSelected={isSelected}
                    multiSelect={!isSingleSelect}
                    onClick={() => pickOption(option)}
                  />
                </Fragment>
              );
            })}

            {question.allow_free_text && (
              <>
                {question.options.length > 0 && <RowDivider />}
                <FreeTextRow
                  value={answer?.freeText ?? ''}
                  multiSelect={!isSingleSelect}
                  onChange={(freeText) =>
                    askUserStore.setFreeText(
                      requestId,
                      question.id,
                      freeText,
                      question.allow_multiple
                    )
                  }
                >
                  {isSingleSelect && <SkipButton onClick={skipQuestion} />}
                </FreeTextRow>
              </>
            )}

            {isSingleSelect && !question.allow_free_text && (
              <XStack className="justify-end px-2.5 pt-2">
                <SkipButton onClick={skipQuestion} />
              </XStack>
            )}
          </YStack>

          {!isSingleSelect && (
            <XStack className="border-border items-center justify-between border-t px-4 py-2">
              <span className="text-foreground-muted text-sm">
                {pickedCount} selected
              </span>
              <XStack className="items-center gap-2">
                <SkipButton onClick={skipQuestion} />
                <button
                  type="button"
                  data-testid="ask-user-done"
                  disabled={pickedCount === 0}
                  onClick={advanceOrComplete}
                  className="focus-ring flex h-8 shrink-0 items-center justify-center rounded-lg bg-gray-12 px-3 text-gray-1 transition-opacity disabled:opacity-50"
                >
                  {isLastQuestion ? (
                    <Icons.send className="size-4" />
                  ) : (
                    <Icons.navigateRight className="size-4" />
                  )}
                </button>
              </XStack>
            </XStack>
          )}
        </fieldset>
      </YStack>
    );
  }
);

const AskUserQuestionPager = ({
  currentIndex,
  total,
  showComplete,
  canComplete,
  onPrevious,
  onNext,
  onComplete,
}: {
  currentIndex: number;
  total: number;
  showComplete: boolean;
  canComplete: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onComplete?: () => void;
}) => {
  if (total <= 1) return null;

  return (
    <>
      <IconButton
        icon={Icons.chevronLeft}
        size="xs"
        variant="ghost"
        data-testid="ask-user-prev"
        disabled={currentIndex === 0}
        onClick={onPrevious}
      />
      <span className="text-sm tabular-nums">
        {currentIndex + 1} of {total}
      </span>
      {showComplete ? (
        <IconButton
          icon={Icons.check}
          size="xs"
          variant="ghost"
          data-testid="ask-user-done"
          disabled={!canComplete}
          onClick={onComplete}
        />
      ) : (
        <IconButton
          icon={Icons.chevronRight}
          size="xs"
          variant="ghost"
          data-testid="ask-user-next"
          disabled={currentIndex >= total - 1}
          onClick={onNext}
        />
      )}
    </>
  );
};

const SkipButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="focus-ring text-foreground-muted hover:text-foreground flex h-8 shrink-0 items-center rounded-lg bg-surface-hover px-3 text-sm transition-colors hover:bg-surface-active"
  >
    Skip
  </button>
);
